import { useState } from "react";
import styled from "@emotion/styled";
import * as Sentry from "@sentry/react";
import { Flex, Text } from "styles/utility-components";

import { useDocumentUploadProgress } from "components/content_library/DocumentUploadProgressProvider";
import Button from "components/ui/button/Button";
import toast from "components/ui/toast/Toast";
import { useCreateManagedSync } from "lib/hooks/api/integrations/useCreateManagedSync";
import { useSubscription } from "lib/hooks/subscription";
import buildCreateSyncOptionsFromFiles from "lib/integrations/buildCreateSyncOptionsFromFiles";
import { AuthenticatedConnectUser, FilePickerInitOptions, paragon } from "lib/integrations/paragon";
import { usePaywall } from "lib/providers/Paywall";
import { EventNames, useTracking } from "lib/tracking";
import { CMS_LEARN_MORE_CONTENT } from "./cmsLearnMoreContent";
import CMSLearnMoreModal from "./CMSLearnMoreModal";
import DisconnectButton from "./DisconnectButton";
import { ProviderOption, ProviderType } from "./types";

type Props = {
  integration: ProviderOption;
  setSyncingProvider: (provider: ProviderType) => void;
};

/**
 * Connects a cloud-storage provider through Paragon, then drives managed document syncs through
 * app-service — a file picker rather than a field mapping, and a different backend.
 *
 * Not merged with CRMConnectionCard for that reason: the two share a look and the Paragon connect
 * step, and nothing else.
 */
export default function CMSConnectionCard({ integration, setSyncingProvider }: Props): JSX.Element {
  const { logEvent } = useTracking();
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);
  const learnMoreContent = CMS_LEARN_MORE_CONTENT[integration.type];

  const { mutateAsync: createSync } = useCreateManagedSync();
  const { setSync } = useDocumentUploadProgress();

  const { openPaywallModal } = usePaywall();
  const subscription = useSubscription();
  const hasBidAccess = subscription.hasDataTypes("BIDS");

  const openFilePicker = async () => {
    try {
      let picker = new paragon.ExternalFilePicker(integration.type, {
        allowMultiSelect: true,
        // Folder selection should be explicitly enabled for Google Drive, it works
        // correctly for SharePoint without this flag
        allowFolderSelect: integration.type === "googledrive" || undefined,
        onFileSelect: async (files) => {
          try {
            const { createManagedSync } = await createSync(
              buildCreateSyncOptionsFromFiles(integration.type, files),
            );
            setSync({ id: createManagedSync.id, integration: integration.type });
            setSyncingProvider(integration.type);
          } catch {
            toast.error("Failed to create sync for selected files. Please try again.");
          }
        },
      });

      switch (integration.type) {
        case "googledrive": {
          await picker.init({
            appId: window.googleProjectId || "",
            developerKey: window.googlePickerApiKey || "",
          });
          break;
        }

        case "sharepoint": {
          const user = paragon.getUser() as AuthenticatedConnectUser;
          const selectedCredentialId = user?.integrations?.sharepoint?.credentialId;
          if (!selectedCredentialId) {
            openConnectWindow();
            toast.info("Please reconnect your SharePoint account to manage access");
            return;
          }

          await picker.init({ selectedCredentialId } as FilePickerInitOptions);
          break;
        }

        default:
          await picker.init({} as FilePickerInitOptions);
      }

      picker.open();
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to open file picker. Please try again.");
    }
  };

  const openConnectWindow = () => {
    try {
      paragon.connect(integration.type, {
        onSuccess: async () => {
          paragon.closePortal();
          await openFilePicker();
        },
      });
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to open connection window. Please try again.");
    }
  };

  return (
    <>
      <ConnectionCard>
        <Flex column gap={8}>
          <IntegrationIcon src={integration.icon} alt={`${integration.name} icon`} />
          <Text h3>{integration.name}</Text>
          <Text description>Connect to your {integration.name} account</Text>
        </Flex>
        <Flex justifyContent="space-between">
          {integration.isEnabled ? (
            <>
              <DisconnectButton integration={integration.type} />
              <Button
                icon="settings01"
                iconProps={{ color: "sysTextDefault" }}
                buttonType="secondary"
                onClick={openFilePicker}
              >
                Configure
              </Button>
            </>
          ) : (
            <>
              <Button
                icon="plus"
                onClick={() => {
                  if (!hasBidAccess) {
                    openPaywallModal("CMS_INTEGRATIONS");
                    return;
                  }

                  logEvent(EventNames.connectToIntegrationClicked, {
                    "Context source": "Integration card",
                    Provider: integration.type,
                  });
                  openConnectWindow();
                }}
              >
                Connect
              </Button>
              {/* Only the cloud storage providers have help centre copy to show. */}
              {learnMoreContent && (
                <Button
                  buttonType="secondary"
                  onClick={() => {
                    logEvent(EventNames.learnMoreIntegrationModalOpened, {
                      "Context source": "Integration card",
                      Provider: integration.type,
                    });
                    setIsLearnMoreOpen(true);
                  }}
                >
                  Learn more
                </Button>
              )}
            </>
          )}
        </Flex>
      </ConnectionCard>
      {learnMoreContent && (
        <CMSLearnMoreModal
          content={learnMoreContent}
          open={isLearnMoreOpen}
          onOpenChange={setIsLearnMoreOpen}
        />
      )}
    </>
  );
}

const ConnectionCard = styled(Flex)(({ theme }) => ({
  backgroundColor: "#ffffff",
  height: "300px",
  width: "285px",
  border: `2px solid ${theme.colors.sysBorderDecorative}`,
  padding: "24px",
  flexDirection: "column",
  justifyContent: "space-between",
}));

const IntegrationIcon = styled.img({
  width: 30,
  height: 30,
});
